import { yupResolver } from '@hookform/resolvers/yup'
import { find, findIndex, forEach, map } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { TransferVouchers } from './TransferVouchers'
import { getTranslation } from '../../../account/services/auth'
import { Icon } from '../../../common/components/Icon'
import Loader from '../../../common/components/Loader'
import {
  CountryContent,
  FestivalDay,
  Gradient,
  Languages,
  Ticket,
  TicketCategory,
  TicketList,
  UsableExtras,
  Vouchers,
} from '../../../common/models'
import { TicketExtras } from '../../../common/models/ticket'
import FormGroup from '../../../core/components/FormGroup'
import { formatDateTime } from '../../../core/constants/times'
import { getFestivalDays } from '../../../core/services/dates-settings'
import { getUsableExtra } from '../../../core/services/extras'
import {
  getFinancialSettingsCategory,
  getSettingsColors,
  getSettingsTickets,
} from '../../../core/services/festival-settings'
import {
  IFestivalMenuItem,
  getFestivalsMenu,
} from '../../../core/services/menu'
import {
  createTicket,
  deleteTicketById,
  getAllCategories,
  getCategory,
  getTicketsByCategoryId,
  ticketVouchers,
  updateTicketById,
} from '../../../core/services/tickets'
import translate from '../../../core/services/translate'
import { ColorCircle } from '../ColorCircle'
import { MessageBox } from '../messages/MessageBox'
import {
  buildTicketPayload,
  IValues,
  ticketDefaultValues,
  ticketSchema,
} from './createTicketModal.logic'
import {
  Button,
  Card,
  Checkbox,
  DateTimePicker,
  Input,
  Modal,
  Textarea,
} from '@/components/ui'
import { PageTitle } from '@/components'
import SelectField from '@/components/select-field'
import { FinancialSettingsCategory } from '@/common/models/settings'
import { untoldEmails } from '@/core/constants'
import { useAppSelector } from '@/store.ts'
import { getCampaigns, ICampaign } from '@/core/services/campaign'

export interface CreateTicketModalProps {
  ticket?: Ticket
  visible: boolean
  setVisible: (show: boolean) => void
  sortBy?: string
  setTickets: (t: TicketList[]) => void
  categoryId: string
  category?: TicketCategory
  allCategories?: TicketCategory[]
  size: number
  page: number
  setTotalElemenets: (t: number) => void
  currency: string
  allLanguages: Languages[]
}

interface IndexFetivalCategoryTickets {
  categories?: TicketCategory[]
  festival?: IFestivalMenuItem
  festivalName: string
  tickets?: TicketList[]
}

const DEFAULT_CAMPAIGN_VALUE = 'default'

export const CreateTicketModal = (
  props: CreateTicketModalProps,
): JSX.Element => {
  const {
    visible,
    setVisible,
    ticket,
    sortBy,
    setTickets,
    categoryId,
    category,
    allCategories: categories,
    currency,
    allLanguages,
  } = props

  const language = getTranslation()

  const [festivalDays, setFestivalDays] = useState<FestivalDay[]>([])
  const [allAddons, setAllAddons] = useState<UsableExtras[]>([])
  const [activeAddons, setActiveAddons] = useState<TicketExtras[]>([])
  const [festivals, setFestivals] = useState<IFestivalMenuItem[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [groupCategoryTickets, setGroupCategoryTickets] = useState<{
    [key: string | number]: IndexFetivalCategoryTickets
  }>({})
  const [upgradeCategoryTickets, setUpgradeCategoryTickets] = useState<{
    [key: string | number]: IndexFetivalCategoryTickets
  }>({})
  const [voucherTransferModal, setVoucherTransferModal] =
    useState<boolean>(false)
  const [fromTicketId, setFromTicketId] = useState<string>('')
  const [toTicketId, setToTicketId] = useState<string>('')
  const [ticketVouchersList, setTicketVouchersList] = useState<Vouchers[]>([])
  const params = useParams()
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [dubaiIntegration, setDubaiIntegration] = useState<boolean>(false)
  const [colors, setColors] = useState<Gradient[]>([])

  const [availableCampaigns, setAvailableCampaigns] = useState<ICampaign[]>([])

  const { user } = useAppSelector((s) => s.app)

  const [taxCategories, setTaxCategories] = useState<
    FinancialSettingsCategory[]
  >([])

  const getTaxCategories = useCallback(async () => {
    try {
      const { data } = await getFinancialSettingsCategory()
      setTaxCategories(data)
    } catch (e) {
      if (e instanceof Error) {
        if (
          (e as any).status === 404 &&
          untoldEmails.includes(user?.email ?? '')
        ) {
          return
        }
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }, [])

  const getSettings = useCallback(async () => {
    try {
      const { data, status } = await getSettingsTickets()
      if (status === 200) {
        setDubaiIntegration(data.dubaiIntegration || false)
        setValue('dubaiIntegration', data.dubaiIntegration || false)
      }
    } catch (e) {
      setDubaiIntegration(false)
      setValue('dubaiIntegration', false, {
        shouldValidate: true,
      })
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }, [])

  const populateCategory = async (id: string) => {
    try {
      const response = await getCategory(id)
      setValue(
        'installment.nrMaxPayments',
        response.data.paymentsConfig?.installmentConfig?.nrMaxPayments || 0,
      )
      setValue(
        'installment.firstInstallmentPercentage',
        response.data.paymentsConfig?.installmentConfig
          ?.firstInstallmentPercentage,
      )
      setValue(
        'installment.firstInstallmentValue',
        response.data.paymentsConfig?.installmentConfig?.firstInstallmentValue,
      )
      setValue(
        'installment.feePercentage',
        response.data.paymentsConfig?.installmentConfig?.feePercentage,
      )
      setValue(
        'installment.feeValue',
        response.data.paymentsConfig?.installmentConfig?.feeValue,
      )
      setValue(
        'bonus.bonusPercentage',
        response.data.paymentsConfig?.bonusConfig?.bonusPercentage,
      )
      setValue(
        'bonus.bonusValue',
        response.data.paymentsConfig?.bonusConfig?.bonusValue,
      )
      setValue(
        'bonus.bonusAvailableDays',
        response.data.paymentsConfig?.bonusConfig?.bonusAvailableDays,
      )
      if (response.data.paymentsConfig?.maxPerOrder > 0) {
        setValue('maxPerOrder', response.data.paymentsConfig?.maxPerOrder)
        setValue('maxPerOrderUnlimited', false)
      } else {
        setValue('maxPerOrder', null)
        setValue('maxPerOrderUnlimited', true)
      }
      if (response.data.paymentsConfig?.sellingCutOffTimestamp) {
        setValue(
          'canBeSoldTillDateTimestamp',
          formatDateTime(response.data.paymentsConfig?.sellingCutOffTimestamp),
        )
      }
      if (response.data.paymentsConfig?.installmentConfig?.installmentEndDate) {
        setValue(
          'installment.installmentEndDate',
          formatDateTime(
            response.data.paymentsConfig?.installmentConfig?.installmentEndDate,
          ),
        )
      }
      if (response.data.paymentsConfig?.bonusConfig?.bonusExpiryDate) {
        setValue(
          'bonus.bonusExpiryDate',
          formatDateTime(
            response.data.paymentsConfig?.bonusConfig?.bonusExpiryDate,
          ),
        )
      }
      if (response.data.color) {
        setValue('color', response.data.color.name)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const getAvailableCampaigns = useCallback(async () => {
    try {
      const { data } = await getCampaigns()
      setAvailableCampaigns(data)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }, [])

  useEffect(() => {
    if (visible) {
      getAvailableCampaigns()
    }
  }, [getAvailableCampaigns, visible])

  useEffect(() => {
    populateCategory(categoryId)
  }, [])

  useEffect(() => {
    if (visible) {
      getSettings()
    }
  }, [getSettings, visible])

  useEffect(() => {
    if (visible) {
      if (untoldEmails.includes(user?.email ?? '')) {
        return
      }
      getTaxCategories()
    }
  }, [getTaxCategories, visible, user])

  useEffect(() => {
    if (!visible) {
      return
    }
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const [allDays, response2, response3, resp] = await Promise.all([getFestivalDays(params.editionId as string), getUsableExtra(), getFestivalsMenu(), getSettingsColors()])

        const fest: FestivalDay[] = map(allDays.data, data => {
          let name = find(data.translations, t => t.locale === 'en')?.name
          if (!name) {
            name = data.translations[0].name
          }
          return {
            id: data.id,
            name: name,
            startingTimestamp: data.startAt,
            endTimestamp: data.endAt,
          }
        })
        setFestivalDays(fest)
        setAllAddons(response2.data)
        setFestivals(response3.data)

        const main = find(resp.data, data => data.name === 'PRIMARY')
        const secondary = find(resp.data, data => data.name === 'SECONDARY')
        const ternary = find(resp.data, data => data.name === 'TERTIARY')
        const temp = []
        if (main?.gradients) {
          temp.push(...main.gradients)
        }
        if (secondary?.gradients) {
          temp.push(...secondary.gradients)
        }
        if (ternary?.gradients) {
          temp.push(...ternary.gradients)
        }

        setColors(temp)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [visible])

  useEffect(() => {
    if (visible && categoryId) {
      setValue('category', categoryId, {
        shouldValidate: true,
      })
    }
  }, [visible, categoryId])

  useEffect(() => {
    const temp = map(allLanguages, (lang) => {
      return {
        locale: lang.locale,
        name: '',
        description: '',
      }
    })
    setValue('content', temp)
    if (visible && ticket) {
      if (!!ticket.translations && ticket.translations.length > 0) {
        setTimeout(() => {
          map(ticket.translations, (trans) => {
            const index = findIndex(
              allLanguages,
              (l) => l.locale === trans.locale,
            )
            if (index !== -1) {
              setValue(`content.${index}.locale`, trans.locale)
              setValue(`content.${index}.name`, trans.name)
              setValue(`content.${index}.description`, trans.description)
            }
          })
        }, 100)
      }
      setTimeout(() => {
        if (ticket.combos) {
          forEach(ticket.combos, (combo, index) => {
            new Promise(async (resolve) => {
              resolve(
                getFestivalCategories(index, combo.festivalEdition.id, false),
              )
            }).then(() => {
              getFestivalCategoriesTickets(index, combo.category.id, false)
            })
          })
        }
        if (ticket.upgrades) {
          forEach(ticket.upgrades, (upgrade, index) => {
            new Promise(async (resolve) => {
              resolve(
                getFestivalUpgradeCategories(
                  index,
                  upgrade.festivalEdition.id,
                  false,
                ),
              )
            }).then(() => {
              getFestivalUpgradeCategoriesTickets(
                index,
                upgrade.category.id,
                false,
              )
            })
          })
        }
        setValue('groupTickets', ticket.combos)
        setValue('upgradeTickets', ticket.upgrades)
      }, 100)
      setTimeout(() => {
        const temp = map(ticket.extras, (extra) => {
          return {
            addonId: extra.extrasId,
            name: extra.name,
            description: extra.description,
            price: extra.price,
            startTimestamp: extra.startTimestamp,
            endTimestamp: extra.endTimestamp,
            countryDependentPrice: extra.countryDependentPrice,
            countryPrices: extra.countryPrices,
          }
        })
        setValue('addons', temp)
      }, 300)
      setActiveAddons(ticket.extras)
      setValue('color', ticket.color.name)
      setValue('intownCheckInCode', ticket.intownTicketId)
      setValue('ordinal', ticket.ordinal)
      setValue('published', ticket.published)
      setValue('externalSell', ticket.externalSell)
      setValue('showOnWebsite', !ticket.hidden)
      setValue('manualSoldOut', ticket.manualSoldOut)
      setValue('category', ticket.ticketCategory.id)
      setValue('price', ticket.price)
      setValue('discount', ticket.discountPrice)
      setValue('backstageProductId', ticket.backstageProductId)
      setValue('backstageSync', ticket.backstageSync)
      setValue('checkinEnabled', ticket.checkinEnabled)
      setValue('alcatrazIntegratable', ticket.alcatrazIntegratable)
      setValue('alcatrazFacilityCode', ticket.alcatrazFacilityCode)
      setValue('corporate', ticket.corporate)
      setValue('supportScanning', ticket.supportScanning)
      setValue('transferTax', ticket.transferTax)
      setValue('onlyForReferral', ticket.onlyForReferral)
      setValue('dubaiArea', ticket.dubaiArea || '')
      setValue('dubaiPriceTypeCode', ticket.dubaiPriceTypeCode || '')
      setValue('dubaiCodePerTicket', ticket.codePerTicket || false)
      setValue(
        'dubaiCodePerContainedTicket',
        ticket.codePerContainedTicket || false,
      )
      setValue('dubaiCompensation', ticket.registerCompFree || false)
      if (ticket.discountPriceEndTimestamp) {
        setValue(
          'discountUntil',
          formatDateTime(ticket.discountPriceEndTimestamp),
        )
      }
      if (!ticket.unlimited || ticket.stock >= 0) {
        setValue('stock', ticket.stock)
        setValue('unlimited', false)
      }
      if (ticket.stock < 0 || ticket.unlimited) {
        setValue('unlimited', ticket.unlimited)
      }
      if (ticket.festivalDay?.id) {
        setValue('dayTicket', ticket.festivalDay?.id)
      }
      if (ticket.paymentsConfig?.maxPerOrder > 0) {
        setValue('maxPerOrder', ticket.paymentsConfig.maxPerOrder)
        setValue('maxPerOrderUnlimited', false)
      } else {
        setValue('maxPerOrder', null)
        setValue('maxPerOrderUnlimited', true)
      }
      if (ticket.publishTimestamp) {
        setValue('publishedDate', formatDateTime(ticket.publishTimestamp))
      }
      if (ticket.unpublishTimestamp) {
        setValue('unpublishedDate', formatDateTime(ticket.unpublishTimestamp))
      }
      if (ticket.soldOutTimestamp) {
        setValue('soldOutDate', formatDateTime(ticket.soldOutTimestamp))
      }
      setValue(
        'paymentOption',
        find(
          ['BOTH', 'FULL_PRICE', 'MONTHLY'],
          (e) => e === ticket.paymentsConfig.paymentOption.name,
        ),
      )
      if (ticket.paymentsConfig?.sellingCutOffTimestamp) {
        setValue(
          'canBeSoldTillDateTimestamp',
          formatDateTime(ticket.paymentsConfig?.sellingCutOffTimestamp),
        )
      }
      if (ticket.paymentsConfig?.installmentConfig) {
        if (ticket.paymentsConfig?.installmentConfig?.installmentEndDate) {
          setValue(
            'installment.installmentEndDate',
            formatDateTime(
              ticket.paymentsConfig?.installmentConfig?.installmentEndDate,
            ),
          )
        }
        if (ticket.paymentsConfig?.installmentConfig?.nrMaxPayments) {
          setValue(
            'installment.nrMaxPayments',
            ticket.paymentsConfig?.installmentConfig?.nrMaxPayments || 0,
          )
        } else {
          setValue('installment.nrMaxPayments', 0)
        }
        setValue(
          'installment.firstInstallmentPercentage',
          ticket.paymentsConfig?.installmentConfig?.firstInstallmentPercentage,
        )
        setValue(
          'installment.firstInstallmentValue',
          ticket.paymentsConfig?.installmentConfig?.firstInstallmentValue,
        )
        setValue(
          'installment.feePercentage',
          ticket.paymentsConfig?.installmentConfig?.feePercentage,
        )
        setValue(
          'installment.feeValue',
          ticket.paymentsConfig?.installmentConfig?.feeValue,
        )
      }
      if (ticket.paymentsConfig?.bonusConfig) {
        setValue(
          'bonus.bonusPercentage',
          ticket.paymentsConfig?.bonusConfig?.bonusPercentage,
        )
        setValue(
          'bonus.bonusValue',
          ticket.paymentsConfig?.bonusConfig?.bonusValue,
        )
        if (ticket.paymentsConfig?.bonusConfig?.bonusExpiryDate) {
          setValue(
            'bonus.bonusExpiryDate',
            formatDateTime(ticket.paymentsConfig?.bonusConfig?.bonusExpiryDate),
          )
        }
        setValue(
          'bonus.bonusAvailableDays',
          ticket.paymentsConfig?.bonusConfig?.bonusAvailableDays,
        )
      }
      if (ticket.taxCategory?.id) {
        setValue('taxCategory.id', ticket.taxCategory.id)
      }

      if (ticket.campaign) {
        setValue('campaign', {
          id: ticket.campaign.id,
          name: ticket.campaign.name,
        })
      }
    }

    return () => {
      setValue('groupTickets', [])
      setValue('upgradeTickets', [])
      reset()
    }
  }, [ticket, visible])

  const handleClose = () => {
    setValue('groupTickets', [])
    setValue('upgradeTickets', [])
    reset()
    setVisible(false)
  }

  const {
    handleSubmit,
    control,
    register,
    resetField,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IValues>({
    mode: 'onChange',
    resolver: yupResolver<IValues>(ticketSchema),
    defaultValues: ticketDefaultValues,
  })

  const getFestivalCategories = async (
    index: number,
    val: string,
    clearOld = true,
  ) => {
    let fest = find(festivals, (fest) => fest.id === val)
    if (!fest) {
      fest = find(festivals, (fest) => {
        return fest.editions.filter((e) => e.id === val).length > 0
      })
    }

    if (fest) {
      setValue(`groupTickets.${index}.festivalEdition.name`, fest.platformName)
      if (clearOld) {
        setValue(`groupTickets.${index}.category.id`, '')
        setValue(`groupTickets.${index}.ticket.id`, '')
      }
    }

    try {
      const { data, status } = await getAllCategories(
        { size: 10000 },
        fest?.platformName,
      )

      if (status === 200) {
        setGroupCategoryTickets((old) => ({
          ...old,
          [index]: {
            festivalName: val,
            categories: data.content,
            tickets: [],
          },
        }))
      }
    } catch (err) {}
  }

  const getFestivalUpgradeCategories = async (
    index: number,
    val: string,
    clearOld = true,
  ) => {
    let fest = find(festivals, (fest) => fest.id === val)
    if (!fest) {
      fest = find(festivals, (fest) => {
        return fest.editions.filter((e) => e.id === val).length > 0
      })
    }
    if (fest) {
      setValue(
        `upgradeTickets.${index}.festivalEdition.name`,
        fest.platformName,
      )
      if (clearOld) {
        setValue(`upgradeTickets.${index}.category.id`, '')
        setValue(`upgradeTickets.${index}.ticket.id`, '')
      }
    }
    try {
      const { data, status } = await getAllCategories(
        { size: 10000 },
        fest?.platformName,
      )

      if (status === 200) {
        setUpgradeCategoryTickets((old) => ({
          ...old,
          [index]: {
            festivalName: val,
            categories: data.content,
            tickets: [],
          },
        }))
      }
    } catch (err) {}
  }

  const getFestivalCategoriesTickets = async (
    index: number,
    val: string,
    clearOld = true,
  ) => {
    const category = find(
      groupCategoryTickets?.[index]?.categories,
      (cat) => cat.id === val,
    )

    if (category) {
      setValue(`groupTickets.${index}.category.id`, category.id)
      setValue(`groupTickets.${index}.category.name`, category.name)
      if (clearOld) {
        setValue(`groupTickets.${index}.ticket.id`, '', {
          shouldValidate: true,
        })
      }
    }
    try {
      const { data, status } = await getTicketsByCategoryId(
        val as string,
        {
          size: 10000,
        },
        false,
      )

      if (status === 200) {
        const tickets = data.content.sort((a, b) => {
          if (a.published === b.published) {
            if (a.published) {
              return a.ordinal - b.ordinal
            } else {
              if (a.publishTimestamp === b.publishTimestamp) {
                return a.ordinal - b.ordinal
              } else {
                return a.publishTimestamp < b.publishTimestamp ? -1 : 1
              }
            }
          } else {
            return a.published < b.published ? 1 : -1
          }
        })
        setGroupCategoryTickets((old) => ({
          ...old,
          [index]: {
            ...old[index],
            tickets: tickets,
          },
        }))
      }
    } catch (err) {}
  }

  const getFestivalUpgradeCategoriesTickets = async (
    index: number,
    val: string,
    clearOld = true,
  ) => {
    const category = find(
      upgradeCategoryTickets?.[index]?.categories,
      (cat) => cat.id === val,
    )

    if (category) {
      setValue(`upgradeTickets.${index}.category.id`, category.id)
      setValue(`upgradeTickets.${index}.category.name`, category.name)
      if (clearOld) {
        setValue(`upgradeTickets.${index}.ticket.id`, '', {
          shouldValidate: true,
        })
      }
    }
    try {
      const { data, status } = await getTicketsByCategoryId(
        val as string,
        {
          size: 10000,
        },
        false,
      )

      if (status === 200) {
        const tickets = data.content.sort((a, b) => {
          if (a.published === b.published) {
            if (a.published) {
              return a.ordinal - b.ordinal
            } else {
              if (a.publishTimestamp === b.publishTimestamp) {
                return a.ordinal - b.ordinal
              } else {
                return a.publishTimestamp < b.publishTimestamp ? -1 : 1
              }
            }
          } else {
            return a.published < b.published ? 1 : -1
          }
        })
        setUpgradeCategoryTickets((old) => ({
          ...old,
          [index]: {
            ...old[index],
            tickets: tickets,
          },
        }))
      }
    } catch (err) {}
  }

  const {
    fields: groupFields,
    append: groupAppend,
    remove: groupRemove,
  } = useFieldArray({
    control,
    name: 'groupTickets',
  })

  useEffect(() => {
    if (groupFields.length > 0) {
      setValue('backstageProductId', null)
    }
  }, [groupFields.length])

  const {
    fields: addonsFields,
    append: addonsAppend,
    remove: addonsRemove,
  } = useFieldArray({
    control,
    name: 'addons',
  })

  const addonWatch = watch('addons')
  const priceWatch = watch('price')
  const colorWatch = watch('color')

  const installmentEndDateWatch = watch('installment.installmentEndDate')
  const nrMaxPaymentsWatch = watch('installment.nrMaxPayments')
  const firstInstallmentPercentageWatch = watch(
    'installment.firstInstallmentPercentage',
  )
  const firstInstallmentValueWatch = watch('installment.firstInstallmentValue')
  const feePercentageWatch = watch('installment.feePercentage')
  const feeValueWatch = watch('installment.feeValue')
  const bonusPercentageWatch = watch('bonus.bonusPercentage')
  const bonusValueWatch = watch('bonus.bonusValue')
  const bonusExpiryDateWatch = watch('bonus.bonusExpiryDate')
  const bonusAvailableDaysWatch = watch('bonus.bonusAvailableDays')
  const unlimitedWatch = watch('unlimited')
  const maxPerOrderUnlimitedWatch = watch('maxPerOrderUnlimited')
  const publishedDateWatch = watch('publishedDate')
  const unpublishedDateWatch = watch('unpublishedDate')

  useEffect(() => {
    if (!!installmentEndDateWatch) {
      setValue('installment.nrMaxPayments', 0)
    }
  }, [installmentEndDateWatch])

  useEffect(() => {
    if (!!nrMaxPaymentsWatch) {
      resetField('installment.installmentEndDate')
    }
  }, [nrMaxPaymentsWatch])

  useEffect(() => {
    if (!!firstInstallmentPercentageWatch) {
      resetField('installment.firstInstallmentValue')
    }
  }, [firstInstallmentPercentageWatch])

  useEffect(() => {
    if (!!firstInstallmentValueWatch) {
      resetField('installment.firstInstallmentPercentage')
    }
  }, [firstInstallmentValueWatch])

  useEffect(() => {
    if (!!feePercentageWatch) {
      resetField('installment.feeValue')
    }
  }, [feePercentageWatch])

  useEffect(() => {
    if (!!feeValueWatch) {
      resetField('installment.feePercentage')
    }
  }, [feeValueWatch])

  useEffect(() => {
    if (!!bonusPercentageWatch) {
      resetField('bonus.bonusValue')
    }
  }, [bonusPercentageWatch])

  useEffect(() => {
    if (!!bonusValueWatch) {
      resetField('bonus.bonusPercentage')
    }
  }, [bonusValueWatch])

  useEffect(() => {
    if (!!bonusExpiryDateWatch) {
      resetField('bonus.bonusAvailableDays')
    }
  }, [bonusExpiryDateWatch])

  useEffect(() => {
    if (!!bonusAvailableDaysWatch) {
      resetField('bonus.bonusExpiryDate')
    }
  }, [bonusAvailableDaysWatch])

  useEffect(() => {
    if (!!unlimitedWatch) {
      resetField('stock')
    }
  }, [unlimitedWatch])

  useEffect(() => {
    if (!!maxPerOrderUnlimitedWatch) {
      resetField('maxPerOrder')
    }
  }, [maxPerOrderUnlimitedWatch])

  const {
    fields: upgradeFields,
    append: upgradeAppend,
    remove: upgradeRemove,
  } = useFieldArray({
    control,
    name: 'upgradeTickets',
  })

  const submit = async (values: IValues) => {
    const data = buildTicketPayload(values, {
      category,
      colors,
      festivalDays,
      allAddons,
      activeAddons,
    })

    setSubmitting(true)
    if (ticket) {
      try {
        const { data: updateTicketData, status } = await updateTicketById(
          ticket.id,
          data,
        )
        const response = await getTicketsByCategoryId(
          categoryId,
          {
            size: 30,
            sortBy,
          },
          false,
        )
        setTickets(response.data.content)
        if (updateTicketData.id !== ticket.id) {
          const { data: ticketVouchersData, status } = await ticketVouchers(
            ticket.id,
          )
          if (status === 200 && ticketVouchersData.length) {
            setToTicketId(updateTicketData.id)
            setFromTicketId(ticket.id)
            handleClose()
            setVoucherTransferModal(true)
            setTicketVouchersList(ticketVouchersData)
            return
          }
        }
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
          handleClose()
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      } finally {
        setSubmitting(false)
      }
    } else {
      try {
        const { status } = await createTicket(data)
        const response = await getTicketsByCategoryId(
          categoryId,
          {
            size: 30,
            sortBy,
          },
          false,
        )
        setTickets(response.data.content)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
          handleClose()
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      } finally {
        setSubmitting(false)
      }
    }
  }

  const handleDelete = () => {
    // prettier-ignore
    (async () => {
      try {
        if (ticket) {
          const { status } = await deleteTicketById(ticket?.id)
          const response = await getTicketsByCategoryId(categoryId, {
            size: 30,
            sortBy,
          }, false)
          setTickets(response.data.content)
          if (status === 204 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
    handleClose()
  }

  const generateClassBasedOnPublish = (ticket: TicketList | undefined) => {
    if (!ticket) return undefined

    if (ticket.published) {
      return 'select-green'
    } else {
      if (ticket.publishTimestamp - new Date().valueOf() <= 0) {
        return 'select-red'
      } else {
        return 'select-yellow'
      }
    }
  }

  const generateSelectClassBasedOnPublish = (
    ticket: string | undefined,
    index: number,
    isCombos: boolean,
  ) => {
    if (!ticket) return undefined
    let t
    if (isCombos) {
      if (!Object.values(groupCategoryTickets)[index]) return undefined
      t = find(
        Object.values(groupCategoryTickets)[index].tickets,
        (tic) => tic.id === ticket,
      )
    } else {
      if (!Object.values(upgradeCategoryTickets)[index]) return undefined
      t = find(
        Object.values(upgradeCategoryTickets)[index].tickets,
        (tic) => tic.id === ticket,
      )
    }

    if (!t) return undefined

    if (t.published) {
      return 'select-green'
    } else {
      if (t.publishTimestamp - new Date().valueOf() <= 0) {
        return 'select-red'
      } else {
        return 'select-yellow'
      }
    }
  }

  const handleChangeAddon = (value: string, index: number) => {
    const selected = find(
      allAddons,
      (addon: UsableExtras) => addon.id === value,
    )
    if (selected) {
      const temp2 = [...addonsFields]
      temp2[index].addonId = selected.id
      temp2[index].name =
        find(selected.translations, (t) => t.locale === 'en')?.name || ''
      temp2[index].description =
        find(selected.translations, (t) => t.locale === 'en')?.description || ''
      temp2[index].price = selected.defaultPrice
      temp2[index].startTimestamp = selected.startTimestamp
      temp2[index].endTimestamp = selected.endTimestamp
      temp2[index].countryDependentPrice = selected.countryDependentPrice
      temp2[index].countryPrices = selected.countryPrices
      setValue(`addons`, temp2)
    }
  }

  return (
    <>
      {(loading || submitting) && <Loader />}
      <Modal
        open={visible}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              title="Tickets"
              subtitle={ticket ? 'Edit Ticket' : 'Add New Ticket'}
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {ticket ? (
              <Button
                variant="destructive"
                onClick={handleDelete}
                type="button"
                className="me-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div className="min-w-10" />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                form="stage-form"
                type="submit"
                loading={submitting}
                disabled={submitting}
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form id="stage-form" onSubmit={handleSubmit(submit)}>
          <div className="space-y-7">
            <h2 className="text-2xl font-semibold text-monochrome-1">
              Main info
            </h2>
            {map(allLanguages, (lang, index) => {
              return (
                <Card
                  className="p-4 space-y-2 bg-monochrome-120 border-none"
                  key={lang.id}
                >
                  <h3 className="text-lg font-semibold text-accent-1">
                    {lang.name}
                  </h3>
                  <div className="space-y-2">
                    <FormGroup
                      label="Ticket Name"
                      icon={
                        <Icon
                          icon="ticket"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          className="text-monochrome-1"
                        />
                      }
                      tooltip={translate(
                        language.value,
                        'tickets.ticket.ticketNameTooltip',
                      )}
                    >
                      <Input
                        isInvalid={!!errors?.content?.[index]?.name?.message}
                        {...register(`content.${index}.name`)}
                        type="text"
                        placeholder="Enter Name"
                      />
                    </FormGroup>
                  </div>
                  <div className="space-y-2">
                    <FormGroup
                      label="Ticket Rule"
                      icon={
                        <Icon
                          icon="ticket-rule"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          className="text-monochrome-1"
                        />
                      }
                      tooltip={translate(
                        language.value,
                        'tickets.ticket.ticketRuleTooltip',
                      )}
                    >
                      <Textarea
                        isInvalid={
                          !!errors?.content?.[index]?.description?.message
                        }
                        {...register(`content.${index}.description`)}
                        placeholder="Enter Description"
                      />
                    </FormGroup>
                  </div>
                </Card>
              )
            })}
            <FormGroup
              label="Category"
              icon={
                <Icon
                  icon="category"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  className="text-monochrome-1"
                />
              }
              tooltip={translate(
                language.value,
                'tickets.ticket.ticketCategoryTooltip',
              )}
            >
              <SelectField
                outline
                placeholder="Select Category"
                value={categoryId}
                onValueChange={(value) => {
                  setValue('category', value)
                  populateCategory(value)
                }}
                options={map(categories, (category) => ({
                  value: category.id,
                  label: category.name,
                }))}
                error={errors?.category?.message}
              />
            </FormGroup>
            <FormGroup
              label="Ticket Tickets Color"
              addon={
                <div className="flex justify-center items-center">
                  <ColorCircle
                    withBorder
                    gradient={find(
                      colors,
                      (color) => color.name === colorWatch,
                    )}
                  />
                </div>
              }
              icon={
                <Icon
                  icon="color"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  className="text-monochrome-1"
                />
              }
              tooltip={translate(
                language.value,
                'tickets.ticket.ticketColorTooltip',
              )}
            >
              <Controller
                name="color"
                control={control}
                render={({ field: { onChange, value, ref } }) => (
                  <SelectField
                    outline
                    placeholder="Select Color"
                    value={value || ''}
                    onValueChange={onChange}
                    options={map(colors, (color) => ({
                      value: color.name,
                      label: color.name,
                    }))}
                    error={errors?.color?.message}
                    ref={ref}
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="Ticket Ordinal"
              tooltip={translate(
                language.value,
                'tickets.ticket.tiketOrdinalTooltip',
              )}
              icon={
                <Icon
                  icon="ordinal"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  className="text-monochrome-1"
                />
              }
            >
              <Input
                isInvalid={!!errors?.ordinal?.message}
                {...register(`ordinal`)}
                type="number"
                min={0}
                placeholder="Enter Ordinal"
              />
            </FormGroup>
            <Controller
              name="canBeSoldTillDateTimestamp"
              control={control}
              render={({ field }) => (
                <FormGroup
                  label="Ticket Can be Sold Until Date (Optional)"
                  tooltip={translate(
                    language.value,
                    'tickets.ticket.tiketSoldUntilTooltip',
                  )}
                  icon={
                    <Icon
                      icon="date"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      className="text-monochrome-1"
                    />
                  }
                >
                  <DateTimePicker
                    value={field.value ? new Date(field.value) : null}
                    onChange={(date) =>
                      field.onChange(date ? date.toISOString() : undefined)
                    }
                    hasError={!!errors?.canBeSoldTillDateTimestamp?.message}
                    placeholder="Enter Date"
                  />
                </FormGroup>
              )}
            />
            <FormGroup
              label="Max Tickets per Order (Optional)"
              tooltip={translate(
                language.value,
                'tickets.ticket.maxTicketsPerOrderTooltip',
              )}
              icon={
                <Icon
                  icon="max-number"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  className="text-monochrome-1"
                />
              }
            >
              <div className="w-full space-y-2">
                <div className="flex items-start gap-2 w-full">
                  {!maxPerOrderUnlimitedWatch && (
                    <Input
                      isInvalid={!!errors?.maxPerOrder?.message}
                      {...register('maxPerOrder')}
                      min={0}
                      type="number"
                      placeholder="Enter Max Tickets per Order"
                    />
                  )}
                  {maxPerOrderUnlimitedWatch && (
                    <Input disabled type="text" value="Unlimited" />
                  )}
                </div>
                <FormGroup
                  label="Unlimited"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                  inputWrapperClassName="w-auto"
                  wrapperClassName="w-auto flex-row-reverse justify-end"
                  labelProps={{
                    htmlFor: 'maxPerOrderUnlimited',
                  }}
                >
                  <Controller
                    name="maxPerOrderUnlimited"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id="maxPerOrderUnlimited"
                        {...register('maxPerOrderUnlimited')}
                        checked={Boolean(maxPerOrderUnlimitedWatch)}
                        onCheckedChange={(checked) => {
                          setValue('maxPerOrderUnlimited', Boolean(checked))
                        }}
                      />
                    )}
                  />
                </FormGroup>
              </div>
            </FormGroup>
            <FormGroup
              label="Stock (Optional)"
              tooltip={translate(
                language.value,
                'tickets.ticket.ticketStockTooltip',
              )}
              icon={
                <Icon
                  icon="basket"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  className="text-monochrome-1"
                />
              }
            >
              <div className="flex flex-col gap-2 w-full mb-4">
                <div className="flex items-start gap-2 w-full">
                  {!unlimitedWatch && (
                    <Input
                      isInvalid={!!errors?.stock?.message}
                      {...register('stock')}
                      min={0}
                      type="number"
                      placeholder="Enter Stock"
                    />
                  )}
                  {unlimitedWatch && (
                    <Input disabled type="text" value="Unlimited" />
                  )}
                </div>

                <FormGroup
                  label="Unlimited"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                  inputWrapperClassName="w-auto"
                  wrapperClassName="w-auto flex-row-reverse justify-end"
                  labelProps={{
                    htmlFor: 'unlimited',
                  }}
                >
                  <Checkbox
                    id="unlimited"
                    checked={Boolean(unlimitedWatch)}
                    onCheckedChange={(checked) => {
                      setValue('unlimited', Boolean(checked))
                    }}
                  />
                </FormGroup>
              </div>
            </FormGroup>
          </div>
          <div className="space-y-7">
            <FormGroup
              label="Day Ticket"
              icon={
                <Icon
                  icon="date-outline"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  className="text-monochrome-1"
                />
              }
            >
              <Controller
                name="dayTicket"
                control={control}
                render={({ field }) => (
                  <SelectField
                    outline
                    placeholder="Select Ticket Type"
                    value={field.value || ''}
                    onValueChange={field.onChange}
                    options={map(festivalDays, (days) => ({
                      value: days.id,
                      label: days.name,
                    }))}
                    error={errors?.dayTicket?.message}
                    ref={field.ref}
                  />
                )}
              />
            </FormGroup>
          </div>
          <hr className="my-4 h-px w-full border-monochrome-120" />
          <div className="space-y-7">
            <h2 className="text-2xl font-semibold text-monochrome-1">
              Add-ons
            </h2>
            {Array.isArray(addonWatch) && addonWatch.length > 0 ? (
              <div className="flex flex-col gap-4">
                {map(addonsFields, (field, index) => {
                  const addonEntry = addonWatch?.[index]
                  const countryPrices = addonEntry?.countryPrices || []
                  const hasCountryPrices = countryPrices.length > 0
                  return (
                    <div
                      key={field.id}
                      className="flex w-full flex-col gap-3 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50 p-4"
                    >
                      <FormGroup
                        label="Select Add-ons"
                        tooltip={translate(
                          language.value,
                          'tickets.ticket.addons.selectAddonTooltip',
                        )}
                        icon={
                          <Icon
                            icon="plug"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            className="text-monochrome-1"
                          />
                        }
                      >
                        <Controller
                          name={`addons.${index}.addonId`}
                          control={control}
                          render={({ field: addonField }) => (
                            <SelectField
                              outline
                              placeholder="Select an add-on"
                              value={addonField.value || ''}
                              onValueChange={(value) => {
                                addonField.onChange(value)
                                handleChangeAddon(value, index)
                              }}
                              options={map(allAddons, (addon) => {
                                const name = find(
                                  addon?.translations,
                                  (translation) => translation.locale === 'en',
                                )?.name
                                return {
                                  value: addon.id,
                                  label: name || addon.id,
                                }
                              })}
                              error={errors?.addons?.[index]?.addonId?.message}
                              ref={addonField.ref}
                            />
                          )}
                        />
                      </FormGroup>
                      {field !== undefined && (
                        <>
                          <hr className="my-2 h-px w-full border-monochrome-120" />
                          {hasCountryPrices ? (
                            <div className="flex flex-col gap-3">
                              {map(
                                countryPrices,
                                (countryPrice: CountryContent, index2) => (
                                  <div
                                    key={countryPrice?.countryId || index2}
                                    className="grid gap-3 md:grid-cols-2"
                                  >
                                    <FormGroup label="Country (default settings)">
                                      <Input
                                        disabled
                                        type="text"
                                        value={countryPrice?.countryName || ''}
                                      />
                                    </FormGroup>
                                    <FormGroup label="Addon Price (default settings)">
                                      <Input
                                        type="number"
                                        defaultValue={countryPrice?.price}
                                        isInvalid={
                                          !!errors?.addons?.[index]
                                            ?.countryPrices?.[index2]?.price
                                            ?.message
                                        }
                                        {...register(
                                          `addons.${index}.countryPrices.${index2}.price`,
                                        )}
                                      />
                                    </FormGroup>
                                  </div>
                                ),
                              )}
                            </div>
                          ) : (
                            <FormGroup label="Default Price (default settings)">
                              <Input
                                type="number"
                                value={addonEntry?.price ?? ''}
                                isInvalid={
                                  !!errors?.addons?.[index]?.price?.message
                                }
                                onChange={(e) => {
                                  setValue(
                                    `addons.${index}.price`,
                                    Number(e.target.value),
                                  )
                                }}
                              />
                            </FormGroup>
                          )}
                          <hr className="my-2 h-px w-full border-monochrome-120" />
                          <div className="grid gap-3 md:grid-cols-2">
                            <FormGroup label="Start Date (default settings)">
                              <DateTimePicker
                                value={
                                  addonEntry?.startTimestamp
                                    ? new Date(addonEntry.startTimestamp)
                                    : null
                                }
                                onChange={(date) => {
                                  setValue(
                                    `addons.${index}.startTimestamp`,
                                    date ? date.valueOf() : 0,
                                  )
                                }}
                                hasError={
                                  !!errors?.addons?.[index]?.startTimestamp
                                    ?.message
                                }
                                placeholder="Select Start Date"
                                maxDate={
                                  addonEntry?.endTimestamp
                                    ? new Date(addonEntry.endTimestamp)
                                    : undefined
                                }
                              />
                            </FormGroup>
                            <FormGroup label="End Date (default settings)">
                              <DateTimePicker
                                value={
                                  addonEntry?.endTimestamp
                                    ? new Date(addonEntry.endTimestamp)
                                    : null
                                }
                                onChange={(date) => {
                                  setValue(
                                    `addons.${index}.endTimestamp`,
                                    date ? date.valueOf() : 0,
                                  )
                                }}
                                hasError={
                                  !!errors?.addons?.[index]?.endTimestamp
                                    ?.message
                                }
                                placeholder="Select End Date"
                                minDate={
                                  addonEntry?.startTimestamp
                                    ? new Date(addonEntry.startTimestamp)
                                    : undefined
                                }
                              />
                            </FormGroup>
                          </div>
                        </>
                      )}
                      {addonsFields.length > 0 && (
                        <Button
                          variant="destructive"
                          type="button"
                          className="w-full"
                          onClick={() => {
                            addonsRemove(index)
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              type="button"
              className="w-full"
              disabled={addonsFields.length >= allAddons.length}
              onClick={() =>
                addonsAppend({
                  addonId: '',
                  name: '',
                  description: '',
                  price: 0,
                  startTimestamp: 0,
                  endTimestamp: 0,
                  countryDependentPrice: false,
                  countryPrices: [],
                })
              }
            >
              Add New
            </Button>
          </div>
          <hr className="my-4 h-px w-full border-monochrome-120" />
          <div className="space-y-7">
            <h2 className="text-2xl font-semibold text-monochrome-1">
              Payment Options
            </h2>
            <FormGroup label="Payment Option">
              <Controller
                name="paymentOption"
                control={control}
                render={({ field }) => (
                  <SelectField
                    outline
                    placeholder="Select Payment Option"
                    value={field.value || ''}
                    onValueChange={field.onChange}
                    options={[
                      {
                        value: 'BOTH',
                        label: 'Both (Full Price, Monthly Payments)',
                      },
                      {
                        value: 'FULL_PRICE',
                        label: 'Full Price',
                      },
                      {
                        value: 'MONTHLY',
                        label: 'Monthly Payments',
                      },
                    ]}
                    error={errors?.paymentOption?.message}
                    ref={field.ref}
                  />
                )}
              />
            </FormGroup>
          </div>
          <hr className="my-4 h-px w-full border-monochrome-120" />
          <div className="space-y-7">
            <h2 className="text-2xl font-semibold text-monochrome-1">Price</h2>
            <FormGroup
              label={`Ticket Price in ${currency}`}
              tooltip={translate(
                language.value,
                'tickets.ticket.ticketPriceTooltip',
                { currency },
              )}
              icon={
                <Icon
                  icon="tag"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  className="text-monochrome-1"
                />
              }
            >
              <Input
                isInvalid={!!errors?.price?.message}
                min={0}
                step={0.01}
                {...register('price')}
                type="number"
                placeholder={`Enter Ticket Price in ${currency}`}
              />
            </FormGroup>
            <FormGroup
              label={`Discount Price in ${currency} (Optional)`}
              tooltip={translate(
                language.value,
                'tickets.ticket.ticketDiscountTooltip',
                { currency },
              )}
              icon={
                <Icon
                  icon="tag-outline"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  className="text-monochrome-1"
                />
              }
            >
              <Input
                isInvalid={!!errors?.discount?.message}
                {...register('discount')}
                type="number"
                step={0.01}
                min={0}
                max={priceWatch}
                placeholder={`Enter Discount Price in ${currency} (Optional)`}
              />
            </FormGroup>
            <Controller
              name="discountUntil"
              control={control}
              render={({ field }) => (
                <FormGroup
                  label="Discount Price is Available Until Date (Optional)"
                  tooltip={translate(
                    language.value,
                    'tickets.ticket.ticketDiscountAvailableTillTooltip',
                  )}
                  icon={
                    <Icon
                      icon="date-outline"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      className="text-monochrome-1"
                    />
                  }
                >
                  <DateTimePicker
                    value={field.value ? new Date(field.value) : null}
                    onChange={(date) =>
                      field.onChange(date ? date.toISOString() : undefined)
                    }
                    hasError={!!errors?.discountUntil?.message}
                    placeholder="Enter Discount Price is Available Until Date (Optional)"
                  />
                </FormGroup>
              )}
            />
          </div>
          <hr className="my-4 h-px w-full border-monochrome-120" />
          <div className="space-y-7">
            <h2 className="text-2xl font-semibold text-monochrome-1">Bonus</h2>
            <div className="flex flex-col gap-3 md:flex-row md:items-end">
              <FormGroup
                label="Bonus in % RUNA (Optional)"
                tooltip={translate(
                  language.value,
                  'tickets.ticket.bonus.bonusPrecentageTooltip',
                )}
              >
                <Input
                  type="number"
                  min={0}
                  max={100}
                  placeholder="Enter Bonus in % RUNA"
                  isInvalid={!!errors?.bonus?.bonusPercentage?.message}
                  {...register('bonus.bonusPercentage')}
                />
              </FormGroup>
              <span className="text-body-medium text-monochrome-2">or</span>
              <FormGroup
                label="Bonus ___ in RUNA"
                tooltip={translate(
                  language.value,
                  'tickets.ticket.bonus.bonusValueTooltip',
                )}
              >
                <Input
                  type="number"
                  placeholder="Enter Amount"
                  min={0}
                  isInvalid={!!errors?.bonus?.bonusValue?.message}
                  {...register('bonus.bonusValue')}
                />
              </FormGroup>
            </div>
            <div className="flex flex-col gap-3 md:flex-row md:items-end">
              <Controller
                name="bonus.bonusExpiryDate"
                control={control}
                render={({ field }) => (
                  <FormGroup
                    label="Bonus - Available Until (Optional)"
                    tooltip={translate(
                      language.value,
                      'tickets.ticket.bonus.bonusAvailableUntillTooltip',
                    )}
                  >
                    <DateTimePicker
                      value={field.value ? new Date(field.value) : null}
                      onChange={(date) =>
                        field.onChange(date ? date.toISOString() : undefined)
                      }
                      hasError={!!errors?.bonus?.bonusExpiryDate?.message}
                      placeholder="Enter Date"
                    />
                  </FormGroup>
                )}
              />
              <span className="text-body-medium text-monochrome-2">or</span>
              <FormGroup label="Number of Days">
                <Input
                  type="number"
                  placeholder="Number of Days"
                  min={0}
                  isInvalid={!!errors?.bonus?.bonusAvailableDays?.message}
                  {...register('bonus.bonusAvailableDays')}
                />
              </FormGroup>
            </div>
          </div>
          <hr className="my-4 h-px w-full border-monochrome-120" />
          <div className="space-y-7">
            <h2 className="text-2xl font-semibold text-monochrome-1">
              Monthly Payment
            </h2>
            <div className="flex flex-col gap-3 md:flex-row md:items-end">
              <Controller
                name="installment.installmentEndDate"
                control={control}
                render={({ field }) => (
                  <FormGroup
                    label="End Date"
                    tooltip={translate(
                      language.value,
                      'tickets.ticket.monthlyPayments.endDateTooltip',
                    )}
                  >
                    <DateTimePicker
                      value={field.value ? new Date(field.value) : null}
                      onChange={(date) =>
                        field.onChange(date ? date.toISOString() : undefined)
                      }
                      hasError={
                        !!errors?.installment?.installmentEndDate?.message
                      }
                      placeholder="Enter Date"
                    />
                  </FormGroup>
                )}
              />
              <span className="text-body-medium text-monochrome-2">or</span>
              <Controller
                name="installment.nrMaxPayments"
                control={control}
                render={({ field }) => (
                  <FormGroup
                    label="Max Payments"
                    tooltip={translate(
                      language.value,
                      'tickets.ticket.monthlyPayments.maxPaymentsTooltip',
                    )}
                  >
                    <SelectField
                      outline
                      placeholder="Select Max Payments"
                      value={field.value ? String(field.value) : ''}
                      onValueChange={(value) =>
                        field.onChange(value ? Number(value) : 0)
                      }
                      options={map(
                        [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                        (n) => ({
                          value: String(n),
                          label: String(n),
                        }),
                      )}
                      error={errors?.installment?.nrMaxPayments?.message}
                      ref={field.ref}
                    />
                  </FormGroup>
                )}
              />
            </div>
            <div className="flex flex-col gap-3 md:flex-row md:items-end">
              <FormGroup
                label="First Rate in %"
                tooltip={translate(
                  language.value,
                  'tickets.ticket.monthlyPayments.firstPaymentPercentageTooltip',
                )}
              >
                <Input
                  type="number"
                  min={0}
                  max={100}
                  placeholder="Enter %"
                  isInvalid={
                    !!errors?.installment?.firstInstallmentPercentage?.message
                  }
                  {...register('installment.firstInstallmentPercentage')}
                />
              </FormGroup>
              <span className="text-body-medium text-monochrome-2">or</span>
              <FormGroup
                label={`First Rate in ${currency}`}
                tooltip={translate(
                  language.value,
                  'tickets.ticket.monthlyPayments.firstRateValueTooltip',
                  { currency },
                )}
              >
                <Input
                  type="number"
                  min={0}
                  placeholder="Enter Amount"
                  isInvalid={
                    !!errors?.installment?.firstInstallmentValue?.message
                  }
                  {...register('installment.firstInstallmentValue')}
                />
              </FormGroup>
            </div>
            <div className="flex flex-col gap-3 md:flex-row md:items-end">
              <FormGroup
                label="Fee in %"
                tooltip={translate(
                  language.value,
                  'tickets.ticket.monthlyPayments.feeInPercentageTooltip',
                )}
              >
                <Input
                  type="number"
                  min={0}
                  max={100}
                  placeholder="Set Fee %"
                  isInvalid={!!errors?.installment?.feePercentage?.message}
                  {...register('installment.feePercentage')}
                />
              </FormGroup>
              <span className="text-body-medium text-monochrome-2">or</span>
              <FormGroup
                label={`Fee in ${currency}`}
                tooltip={translate(
                  language.value,
                  'tickets.ticket.monthlyPayments.feeInValueTooltip',
                  { currency },
                )}
              >
                <Input
                  type="number"
                  min={0}
                  placeholder={`Enter Fee in ${currency}`}
                  isInvalid={!!errors?.installment?.feeValue?.message}
                  {...register('installment.feeValue')}
                />
              </FormGroup>
            </div>
          </div>
          <hr className="my-4 h-px w-full border-monochrome-120" />
          <div className="space-y-7">
            <h2 className="text-2xl font-semibold text-monochrome-1">
              Create Combo / Group Tickets
            </h2>
            <div className="flex flex-col gap-4">
              {map(groupFields, (field, index) => (
                <div
                  key={field.id}
                  className="flex w-full flex-col gap-3 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50 p-4"
                >
                  <div className="grid gap-3 lg:grid-cols-[1fr_1fr_1fr_1fr_auto]">
                    <FormGroup
                      label="Select Festival"
                      tooltip={translate(
                        language.value,
                        'tickets.ticket.combo.selectFestivalTooltip',
                      )}
                    >
                      <Controller
                        name={`groupTickets.${index}.festivalEdition.id`}
                        control={control}
                        render={({ field: festivalField }) => (
                          <SelectField
                            outline
                            placeholder="Select a Festival"
                            value={festivalField.value || ''}
                            onValueChange={(value) => {
                              festivalField.onChange(value)
                              getFestivalCategories(index, value)
                            }}
                            options={festivals.flatMap((festival) => {
                              if (festival.editions.length) {
                                return festival.editions.map((edition) => ({
                                  value: edition.id,
                                  label: `${festival.name} - ${edition.name}`,
                                }))
                              }
                              return [
                                {
                                  value: festival.id,
                                  label: festival.name,
                                },
                              ]
                            })}
                            error={
                              errors?.groupTickets?.[index]?.festivalEdition?.id
                                ?.message
                            }
                            ref={festivalField.ref}
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Select Category"
                      tooltip={translate(
                        language.value,
                        'tickets.ticket.combo.selectCategoryTooltip',
                      )}
                    >
                      <Controller
                        name={`groupTickets.${index}.category.id`}
                        control={control}
                        render={({ field: categoryField }) => (
                          <SelectField
                            outline
                            placeholder="Select a Category"
                            value={categoryField.value || ''}
                            onValueChange={(value) => {
                              categoryField.onChange(value)
                              getFestivalCategoriesTickets(index, value)
                            }}
                            options={map(
                              groupCategoryTickets?.[index]?.categories || [],
                              (category) => ({
                                value: category.id,
                                label: category.name,
                              }),
                            )}
                            error={
                              errors?.groupTickets?.[index]?.category?.id
                                ?.message
                            }
                            ref={categoryField.ref}
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Select Ticket"
                      tooltip={translate(
                        language.value,
                        'tickets.ticket.combo.selectTicketTooltip',
                      )}
                    >
                      <Controller
                        name={`groupTickets.${index}.ticket.id`}
                        control={control}
                        render={({ field: ticketField }) => (
                          <SelectField
                            outline
                            placeholder="Select Ticket"
                            value={ticketField.value || ''}
                            triggerClassName={generateSelectClassBasedOnPublish(
                              ticketField.value,
                              index,
                              true,
                            )}
                            onValueChange={(value) => {
                              ticketField.onChange(value)
                              const ticket = find(
                                groupCategoryTickets[index]?.tickets,
                                (ticket) => ticket.id === value,
                              )
                              if (ticket) {
                                setValue(
                                  `groupTickets.${index}.ticket.id`,
                                  ticket.id,
                                  { shouldValidate: true },
                                )
                                setValue(
                                  `groupTickets.${index}.ticket.name`,
                                  ticket.name,
                                  { shouldValidate: true },
                                )
                              }
                            }}
                            options={map(
                              groupCategoryTickets?.[index]?.tickets || [],
                              (ticket) => ({
                                value: ticket.id,
                                label: ticket.name,
                              }),
                            )}
                            error={
                              errors?.groupTickets?.[index]?.ticket?.id?.message
                            }
                            ref={ticketField.ref}
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup label="Ticket View Price">
                      <Input
                        isInvalid={!!errors?.groupTickets?.[index]?.viewPrice}
                        min={0}
                        step={0.01}
                        {...register(`groupTickets.${index}.viewPrice`)}
                        type="number"
                        placeholder="Enter Ticket Price"
                      />
                    </FormGroup>
                    <Button
                      variant="destructive"
                      type="button"
                      className="w-full mt-4"
                      onClick={() => {
                        groupRemove(index)
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                variant="secondary"
                size="sm"
                type="button"
                className="w-full"
                onClick={() =>
                  groupAppend({
                    festivalEdition: { id: '', name: '' },
                    category: { id: '', name: '' },
                    ticket: { id: '', name: '' },
                  })
                }
              >
                Add New
              </Button>
            </div>
          </div>
          <hr className="my-4 h-px w-full border-monochrome-120" />
          <div className="space-y-7">
            <h2 className="text-2xl font-semibold text-monochrome-1">
              Options for Upgrade Tickets
            </h2>
            <div className="flex flex-col gap-4">
              {map(upgradeFields, (field, index) => (
                <div
                  key={field.id}
                  className="flex w-full flex-col gap-3 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50 p-4"
                >
                  <div className="grid gap-3 lg:grid-cols-[1fr_1fr_1fr_1fr_auto]">
                    <FormGroup
                      label="Select Festival"
                      tooltip={translate(
                        language.value,
                        'tickets.ticket.upgrade.selectFestivalTooltip',
                      )}
                    >
                      <Controller
                        name={`upgradeTickets.${index}.festivalEdition.id`}
                        control={control}
                        render={({ field: festivalField }) => (
                          <SelectField
                            outline
                            placeholder="Select a Festival"
                            value={festivalField.value || ''}
                            onValueChange={(value) => {
                              festivalField.onChange(value)
                              getFestivalUpgradeCategories(index, value)
                            }}
                            options={festivals.flatMap((festival) => {
                              if (festival.editions.length) {
                                return festival.editions.map((edition) => ({
                                  value: edition.id,
                                  label: `${festival.name} - ${edition.name}`,
                                }))
                              }
                              return [
                                {
                                  value: festival.id,
                                  label: festival.name,
                                },
                              ]
                            })}
                            error={
                              errors?.upgradeTickets?.[index]?.festivalEdition
                                ?.id?.message
                            }
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Select Category"
                      tooltip={translate(
                        language.value,
                        'tickets.ticket.upgrade.selectCategoryTooltip',
                      )}
                    >
                      <Controller
                        name={`upgradeTickets.${index}.category.id`}
                        control={control}
                        render={({ field: categoryField }) => (
                          <SelectField
                            outline
                            placeholder="Select a Category"
                            value={categoryField.value || ''}
                            onValueChange={(value) => {
                              categoryField.onChange(value)
                              getFestivalUpgradeCategoriesTickets(index, value)
                            }}
                            options={map(
                              upgradeCategoryTickets?.[index]?.categories || [],
                              (category) => ({
                                value: category.id,
                                label: category.name,
                              }),
                            )}
                            error={
                              errors?.upgradeTickets?.[index]?.category?.id
                                ?.message
                            }
                            ref={categoryField.ref}
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Select Ticket"
                      tooltip={translate(
                        language.value,
                        'tickets.ticket.upgrade.selectTicketTooltip',
                      )}
                    >
                      <Controller
                        name={`upgradeTickets.${index}.ticket.id`}
                        control={control}
                        render={({ field: ticketField }) => (
                          <SelectField
                            outline
                            placeholder="Select Ticket"
                            value={ticketField.value || ''}
                            triggerClassName={generateSelectClassBasedOnPublish(
                              ticketField.value,
                              index,
                              false,
                            )}
                            onValueChange={(value) => {
                              ticketField.onChange(value)
                              const ticket = find(
                                upgradeCategoryTickets[index]?.tickets,
                                (ticket) => ticket.id === value,
                              )
                              if (ticket) {
                                setValue(
                                  `upgradeTickets.${index}.ticket.id`,
                                  ticket.id,
                                  { shouldValidate: true },
                                )
                                setValue(
                                  `upgradeTickets.${index}.ticket.name`,
                                  ticket.name,
                                  { shouldValidate: true },
                                )
                              }
                            }}
                            options={map(
                              upgradeCategoryTickets?.[index]?.tickets || [],
                              (ticket) => ({
                                value: ticket.id,
                                label: ticket.name,
                              }),
                            )}
                            error={
                              errors?.upgradeTickets?.[index]?.ticket?.id
                                ?.message
                            }
                            ref={ticketField.ref}
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup label="Ticket View Price">
                      <Input
                        isInvalid={!!errors?.upgradeTickets?.[index]?.viewPrice}
                        min={0}
                        step={0.01}
                        {...register(`upgradeTickets.${index}.viewPrice`)}
                        type="number"
                        placeholder="Enter Ticket Price"
                      />
                    </FormGroup>
                    <Button
                      variant="destructive"
                      type="button"
                      className="w-full"
                      onClick={() => {
                        upgradeRemove(index)
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                variant="secondary"
                size="sm"
                type="button"
                className="w-full"
                onClick={() =>
                  upgradeAppend({
                    festivalEdition: { id: '', name: '' },
                    category: { id: '', name: '' },
                    ticket: { id: '', name: '' },
                  })
                }
              >
                Add New
              </Button>
            </div>
          </div>
          <hr className="my-4 h-px w-full border-monochrome-120" />
          {dubaiIntegration && (
            <>
              <h2 className="text-2xl font-semibold text-monochrome-1">
                Dubai DET Government API
              </h2>
              <div className="space-y-3">
                <FormGroup label="Area">
                  <Input
                    isInvalid={!!errors?.dubaiArea?.message}
                    {...register(`dubaiArea`)}
                    type="text"
                    placeholder="Area"
                  />
                </FormGroup>
                <FormGroup label="Price type code">
                  <Input
                    isInvalid={!!errors?.dubaiPriceTypeCode?.message}
                    {...register(`dubaiPriceTypeCode`)}
                    type="text"
                    placeholder="Price type code"
                  />
                </FormGroup>
                <Controller
                  name="dubaiCodePerTicket"
                  control={control}
                  render={({ field }) => (
                    <FormGroup
                      label="Code per ticket"
                      direction="row"
                      labelClassName="min-w-[160px]"
                    >
                      <Checkbox
                        ref={field.ref}
                        className="border-monochrome-120"
                        checked={Boolean(field.value)}
                        onCheckedChange={(checked) =>
                          field.onChange(Boolean(checked))
                        }
                        isInvalid={!!errors?.dubaiCodePerTicket?.message}
                      />
                    </FormGroup>
                  )}
                />
                <Controller
                  name="dubaiCodePerContainedTicket"
                  control={control}
                  render={({ field }) => (
                    <FormGroup
                      label="Code per contained ticket"
                      direction="row"
                      labelClassName="min-w-[210px]"
                    >
                      <Checkbox
                        ref={field.ref}
                        className="border-monochrome-120"
                        checked={Boolean(field.value)}
                        onCheckedChange={(checked) =>
                          field.onChange(Boolean(checked))
                        }
                        isInvalid={
                          !!errors?.dubaiCodePerContainedTicket?.message
                        }
                      />
                    </FormGroup>
                  )}
                />
                <Controller
                  name="dubaiCompensation"
                  control={control}
                  render={({ field }) => (
                    <FormGroup
                      label="Compensation"
                      direction="row"
                      labelClassName="min-w-[140px]"
                    >
                      <Checkbox
                        ref={field.ref}
                        className="border-monochrome-120"
                        checked={Boolean(field.value)}
                        onCheckedChange={(checked) =>
                          field.onChange(Boolean(checked))
                        }
                        isInvalid={!!errors?.dubaiCompensation?.message}
                      />
                    </FormGroup>
                  )}
                />
              </div>
              <hr className="my-4 h-px w-full border-monochrome-120" />
            </>
          )}
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-monochrome-1">
              Send as Gift
            </h2>
            <FormGroup label="Transfer Tax">
              <Input
                isInvalid={!!errors?.transferTax?.message}
                {...register(`transferTax`)}
                type="number"
                placeholder="Enter Transfer Tax"
              />
            </FormGroup>
          </div>
          <hr className="my-4 h-px w-full border-monochrome-120" />
          {!untoldEmails.includes(user?.email ?? '') && (
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold text-monochrome-1">
                Taxes
              </h2>
              <FormGroup
                label="Tax Type"
                tooltip="The tax type that will be applied to the ticket"
                labelWrapperClassName="justify-between"
              >
                <Controller
                  name="taxCategory.id"
                  control={control}
                  render={({ field }) => (
                    <SelectField
                      options={taxCategories
                        .filter((tax) => tax.active)
                        .map((tax) => ({
                          label: tax.name,
                          value: tax.id,
                        }))}
                      outline
                      placeholder="Select Tax Type"
                      wrapperClassName="h-[44px]"
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value)
                      }}
                      error={errors?.taxCategory?.message}
                      ref={field.ref}
                    />
                  )}
                />
              </FormGroup>
              {watch('taxCategory.id') && (
                <div className="flex flex-col gap-2">
                  {taxCategories
                    .find((tax) => tax.id === watch('taxCategory.id'))
                    ?.taxes.map((tax) => (
                      <div
                        key={tax.id}
                        className="text-monochrome-1 text-body-medium flex items-center gap-1"
                      >
                        <p>{tax.name}</p>
                        {tax.amountPercentage ? (
                          <p>
                            in % -{' '}
                            <span className="text-accent-1">
                              {tax.amountPercentage}%
                            </span>
                          </p>
                        ) : (
                          <p>
                            fixed -{' '}
                            <span className="text-accent-1">
                              {tax.amountFixed} {currency}
                            </span>
                          </p>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
          <hr className="my-4 h-px w-full border-monochrome-120" />
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-monochrome-1">
              Backstage
            </h2>
            <FormGroup label="Backstage Product ID">
              <Input
                disabled={groupFields.length > 0}
                isInvalid={!!errors?.backstageProductId?.message}
                {...register(`backstageProductId`)}
                type="text"
                placeholder="Enter Backstage Product ID"
              />
            </FormGroup>
            <Controller
              name="backstageSync"
              control={control}
              render={({ field }) => (
                <FormGroup
                  label="Sync this tickets to Backstage / Festipay"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                >
                  <Checkbox
                    ref={field.ref}
                    className="border-monochrome-120"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    isInvalid={!!errors?.backstageSync?.message}
                  />
                </FormGroup>
              )}
            />
            <Controller
              name="checkinEnabled"
              control={control}
              render={({ field }) => (
                <FormGroup
                  label="Requires Checkin"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                >
                  <Checkbox
                    ref={field.ref}
                    className="border-monochrome-120"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    isInvalid={!!errors?.checkinEnabled?.message}
                  />
                </FormGroup>
              )}
            />
          </div>
          <hr className="my-4 h-px w-full border-monochrome-120" />
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-monochrome-1">
              Alcatraz
            </h2>
            <Controller
              name="alcatrazIntegratable"
              control={control}
              render={({ field }) => (
                <FormGroup
                  label="Alcatraz integratable"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                >
                  <Checkbox
                    ref={field.ref}
                    className="border-monochrome-120"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    isInvalid={!!errors?.alcatrazIntegratable?.message}
                  />
                </FormGroup>
              )}
            />
            {watch('alcatrazIntegratable') && (
              <FormGroup
                label="Alcatraz facility code"
                error={errors?.alcatrazFacilityCode?.message}
              >
                <Input
                  isInvalid={!!errors?.alcatrazFacilityCode?.message}
                  {...register('alcatrazFacilityCode')}
                  type="number"
                  placeholder="Enter Alcatraz Facility Code"
                />
              </FormGroup>
            )}
          </div>
          <hr className="my-4 h-px w-full border-monochrome-120" />
          {!dubaiIntegration && (
            <>
              <FormGroup
                label="InTown Check-in Code"
                tooltip={translate(
                  language.value,
                  'tickets.ticket.intownCheckinCodeTooltip',
                )}
                icon={
                  <Icon
                    icon="key"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    className="text-monochrome-1"
                  />
                }
              >
                <Input
                  isInvalid={!!errors?.intownCheckInCode?.message}
                  {...register(`intownCheckInCode`)}
                  type="text"
                  placeholder="Enter check-in Code"
                />
              </FormGroup>
              <hr className="my-4 h-px w-full border-monochrome-120" />
            </>
          )}
          <div className="space-y-7">
            <Controller
              name="publishedDate"
              control={control}
              render={({ field }) => (
                <FormGroup
                  label="Publish Date"
                  tooltip={translate(
                    language.value,
                    'tickets.ticket.publishDateTooltip',
                  )}
                  icon={
                    <Icon
                      icon="date-check"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      className="text-monochrome-1"
                    />
                  }
                >
                  <DateTimePicker
                    value={field.value ? new Date(field.value) : null}
                    onChange={(date) =>
                      field.onChange(date ? date.toISOString() : undefined)
                    }
                    hasError={!!errors?.publishedDate?.message}
                    placeholder="Enter Publish Date"
                    maxDate={
                      unpublishedDateWatch
                        ? new Date(unpublishedDateWatch)
                        : undefined
                    }
                  />
                </FormGroup>
              )}
            />
            <Controller
              name="unpublishedDate"
              control={control}
              render={({ field }) => (
                <FormGroup
                  label="UnPublish Date"
                  tooltip={translate(
                    language.value,
                    'tickets.ticket.unpublishDateTooltip',
                  )}
                  icon={
                    <Icon
                      icon="date-check"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      className="text-monochrome-1"
                    />
                  }
                >
                  <DateTimePicker
                    value={field.value ? new Date(field.value) : null}
                    onChange={(date) =>
                      field.onChange(date ? date.toISOString() : undefined)
                    }
                    hasError={!!errors?.unpublishedDate?.message}
                    placeholder="Enter Unpublish Date"
                    minDate={
                      publishedDateWatch
                        ? new Date(publishedDateWatch)
                        : undefined
                    }
                  />
                </FormGroup>
              )}
            />
            <Controller
              name="soldOutDate"
              control={control}
              render={({ field }) => (
                <FormGroup
                  label="Sold Out Date"
                  tooltip={translate(
                    language.value,
                    'tickets.ticket.soldOutDateTooltip',
                  )}
                  icon={
                    <Icon
                      icon="date-check"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      className="text-monochrome-1"
                    />
                  }
                >
                  <DateTimePicker
                    value={field.value ? new Date(field.value) : null}
                    onChange={(date) =>
                      field.onChange(date ? date.toISOString() : undefined)
                    }
                    hasError={!!errors?.soldOutDate?.message}
                    placeholder="Enter Sold Out Date"
                  />
                </FormGroup>
              )}
            />
            <FormGroup
              label="View on Page"
              tooltip={'The campaign that will be applied to the ticket'}
            >
              <Controller
                name="campaign"
                control={control}
                render={({ field }) => (
                  <SelectField
                    outline
                    placeholder="Select Campaign"
                    value={field.value?.id || DEFAULT_CAMPAIGN_VALUE}
                    onValueChange={(value) => {
                      if (value === DEFAULT_CAMPAIGN_VALUE) {
                        field.onChange(null)
                        return
                      }
                      const campaign = availableCampaigns.find(
                        (campaign) => campaign.id === value,
                      )
                      if (campaign) {
                        field.onChange({
                          id: campaign.id,
                          name: campaign.name,
                        })
                      }
                    }}
                    options={[
                      {
                        value: DEFAULT_CAMPAIGN_VALUE,
                        textValue: 'Default',
                        label: (
                          <span className="text-body-medium text-monochrome-1">
                            Default
                          </span>
                        ),
                      },
                      ...map(availableCampaigns, (campaign) => ({
                        value: campaign.id,
                        textValue: campaign.name,
                        label: (
                          <div className="flex justify-between items-center w-full gap-2">
                            <span className="text-body-medium text-monochrome-1">
                              {campaign.name}
                            </span>
                            <span className="text-body-small text-monochrome-2">
                              /{campaign.url}
                            </span>
                          </div>
                        ),
                      })),
                    ]}
                    error={errors?.campaign?.message}
                    ref={field.ref}
                    wrapperClassName="h-[44px]"
                  />
                )}
              />
            </FormGroup>

            <div className="bg-monochrome-120 my-4 h-px w-full" />
            <div className="space-y-2">
              <Controller
                name="onlyForReferral"
                control={control}
                render={({ field }) => (
                  <FormGroup
                    label="Only For Referral"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                  >
                    <Checkbox
                      ref={field.ref}
                      className="border-monochrome-120"
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      isInvalid={!!errors?.externalSell?.message}
                    />
                  </FormGroup>
                )}
              />
              <Controller
                name="corporate"
                control={control}
                render={({ field }) => (
                  <FormGroup
                    label="Corporate Ticket or Invites"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                  >
                    <Checkbox
                      className="border-monochrome-120"
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                    />
                  </FormGroup>
                )}
              />
              <Controller
                name="supportScanning"
                control={control}
                render={({ field }) => (
                  <FormGroup
                    label="Can be scaned"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                  >
                    <Checkbox
                      ref={field.ref}
                      className="border-monochrome-120"
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      isInvalid={!!errors?.supportScanning?.message}
                    />
                  </FormGroup>
                )}
              />
              <Controller
                name="externalSell"
                control={control}
                render={({ field }) => (
                  <FormGroup
                    label="External Seller"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                  >
                    <Checkbox
                      ref={field.ref}
                      className="border-monochrome-120"
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      isInvalid={!!errors?.externalSell?.message}
                    />
                  </FormGroup>
                )}
              />
              <Controller
                name="showOnWebsite"
                control={control}
                render={({ field }) => (
                  <FormGroup
                    label="Show on website"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                  >
                    <Checkbox
                      ref={field.ref}
                      className="border-monochrome-120"
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      isInvalid={!!errors?.showOnWebsite?.message}
                    />
                  </FormGroup>
                )}
              />
              <Controller
                name="published"
                control={control}
                render={({ field }) => (
                  <FormGroup
                    label="Published"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    inputWrapperClassName="w-auto"
                    wrapperClassName="w-auto flex-row-reverse justify-end"
                  >
                    <Checkbox
                      ref={field.ref}
                      className="border-monochrome-120"
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      isInvalid={!!errors?.published?.message}
                    />
                  </FormGroup>
                )}
              />
            </div>
          </div>
          {/*Disabled manual sold out*/}
          {/*<Form.Group className="flex items-center justify-end w-full">*/}
          {/*  <Form.Label*/}
          {/*    className="flex flex-col mb-0 me-4 w-full"*/}
          {/*    style={{ width: '100px' }}*/}
          {/*  >*/}
          {/*    Sold Out:*/}
          {/*  </Form.Label>*/}
          {/*  <Form.Check*/}
          {/*    isInvalid={!!errors?.manualSoldOut?.message}*/}
          {/*    {...register('manualSoldOut')}*/}
          {/*    type="checkbox"*/}
          {/*  />*/}
          {/*</Form.Group>*/}
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
      <TransferVouchers
        visible={voucherTransferModal}
        setVisible={setVoucherTransferModal}
        vouchers={ticketVouchersList}
        fromTicketId={fromTicketId}
        toTicketId={toTicketId}
      />
    </>
  )
}
